import React, { useCallback, useEffect, useState } from 'react';
import { fetchBanquetRecent, fetchRevertPreview, revertBanquet } from '../../utils/banquetApi';
import {
  lockedEntrySentence,
  revertQuestion,
  revertSuccessHeadline,
  wastageEntryLine,
  wastageIntro,
} from '../../utils/banquetWastageText';

/**
 * Revert a banquet so it can be entered again.
 *
 * WHY THIS EXISTS AT ALL, AND IS NOT DEV-ONLY
 * -------------------------------------------
 * There is no edit in v1, so this is the ONLY way to correct a banquet — a
 * mistyped rate, a plate count that changed after the event. A banquet DOES
 * move stock: it deducts through the TSR engine (provenance 'banquet'), and
 * reverting it puts exactly that stock back (server: services/banquetStock.js,
 * through the same utils/stockRestore TSR and online revert use). The Revert
 * TSR and Revert Online Sale entries beside it in the menu are wrapped in an
 * isLocalhost check; this one stays open to owners because the SERVER is the
 * gate — it enforces the revert window (back to the last confirmed closing
 * count, or 30 days without one), refuses while a closing count is in
 * progress, and locks the rows it touches. Gating it to localhost would leave
 * every real owner unable to fix a typo.
 *
 * A BANQUET WITH WASTAGE REVERTS WITH IT — AFTER THE OWNER HAS SEEN THE LIST.
 * Banquet wastage points at its event, so the event cannot go without it.
 * "Revert this banquet" first asks the server what the revert includes
 * (fetchRevertPreview) and shows exactly one of:
 *   - the preview's error (Close);
 *   - the event's own refusal, in the server's words (Close);
 *   - no wastage: the plain Cancel / Confirm revert;
 *   - wastage, all revertable: the list, then Cancel / Confirm & Revert Both;
 *   - a wastage entry is locked: which one and why (Close only).
 * Confirm sends exactly the ids that were shown. If the list moved meanwhile
 * the server answers BANQUET_WASTAGE_CHANGED and the popup reloads and asks
 * again. The client never decides whether a revert is allowed; it only renders
 * the server's verdicts.
 *
 * THE WINDOW IS SHOWN BEFORE THE LIST, NOT AFTER A REFUSAL.
 * The same reasoning as the TSR revert modal: an owner should understand the
 * boundary while choosing, rather than discover it by being turned down.
 */

const CHANGED_NOTICE = 'Wastage for this banquet changed while you were deciding. Please review again.';

/** The one state the confirm area shows for a preview answer. */
const confirmStateFrom = (res, ev, notice = null) => {
  const base = { eventId: ev.eventId, notice };
  if (!res || !res.ok) {
    return { ...base, status: 'error', error: (res && res.error) || 'Could not load this banquet.' };
  }
  const event = res.event || ev;
  // A payload with no verdict asserts nothing; the revert endpoint re-checks anyway.
  if (res.verdict && res.verdict.allowed === false) {
    return { ...base, status: 'refused', event, reason: res.verdict.reason || 'This banquet can no longer be reverted.' };
  }
  const wastage = Array.isArray(res.wastage) ? res.wastage : [];
  if (wastage.length === 0) return { ...base, status: 'plain', event };

  const locked = wastage.filter((w) => w && w.revert && w.revert.allowed === false);
  if (locked.length > 0) {
    return {
      ...base,
      status: 'locked',
      event,
      blocked: locked.map((w) => ({ ...w, code: w.revert.code, reason: w.revert.reason })),
    };
  }
  const total = Number(res.wastageTotal);
  return {
    ...base,
    status: 'wastage',
    event,
    wastage,
    wastageTotal: Number.isFinite(total) && total > 0 ? total : wastage.reduce((s, w) => s + (Number(w.cost) || 0), 0),
  };
};

const panelStyle = {
  marginTop: '10px', padding: '12px', borderRadius: '8px',
  background: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '0.85rem', color: '#111', lineHeight: 1.5,
};
const noticeStyle = {
  background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: '8px',
  padding: '8px 10px', marginBottom: '10px', fontSize: '0.82rem', fontWeight: 600,
};
const secondaryBtn = {
  flex: 1, padding: '9px', borderRadius: '8px', border: '1.5px solid #d1d5db',
  background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer',
};
const dangerBtn = {
  flex: 1, padding: '9px', borderRadius: '8px', border: 'none',
  background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer',
};

const BanquetRevertModal = ({ onClose, onReverted }) => {
  const [events, setEvents] = useState([]);
  const [window_, setWindow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The open confirm area: { eventId, status, notice, ... } — see confirmStateFrom.
  const [confirm, setConfirm] = useState(null);
  const [revertingId, setRevertingId] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchBanquetRecent();
    if (res.ok) {
      setEvents(res.events);
      setWindow(res.window);
    } else {
      setError(res.error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Ask the server what reverting this banquet would include, then show it. */
  const openConfirm = async (ev, notice = null) => {
    setError(null);
    setConfirm({ eventId: ev.eventId, status: 'loading', notice });
    const res = await fetchRevertPreview(ev.eventId);
    // Ignore a late answer if the owner cancelled or moved to another banquet.
    setConfirm((prev) => (prev && prev.eventId === ev.eventId ? confirmStateFrom(res, ev, notice) : prev));
  };

  const closeConfirm = () => setConfirm(null);

  const doRevert = async (ev) => {
    const eventId = ev.eventId;
    const shown = confirm && confirm.eventId === eventId ? confirm : null;
    const withWastage = shown && shown.status === 'wastage';

    setRevertingId(eventId);
    setError(null);
    const res = withWastage
      ? await revertBanquet(eventId, {
        includeWastage: true,
        expectedWastageIds: shown.wastage.map((w) => w.stockOutId),
      })
      : await revertBanquet(eventId);
    setRevertingId(null);

    if (res.ok) {
      setConfirm(null);
      const reverted = res.reverted || {};
      const wastageReverted = reverted.wastageReverted || res.wastage_reverted || [];
      setSuccess({ ...reverted, wastageCount: wastageReverted.length });
      setEvents((prev) => prev.filter((e) => e.eventId !== eventId));
      if (onReverted) onReverted(res.reverted);
      return;
    }

    // The wastage list moved while the owner was deciding: reload it and ask again.
    if (res.code === 'BANQUET_WASTAGE_CHANGED') {
      await openConfirm(ev, CHANGED_NOTICE);
      return;
    }
    // Wastage the plain confirm did not know about: show it and ask.
    if (res.code === 'BANQUET_HAS_WASTAGE') {
      await openConfirm(ev);
      return;
    }
    // An entry locked since the list was shown. data.blocked names it; the
    // quantity and day come from the list the owner was looking at.
    if (res.code === 'BANQUET_WASTAGE_LOCKED') {
      const listed = (shown && shown.wastage) || [];
      const blocked = ((res.data && res.data.blocked) || []).map((b) => ({
        ...(listed.find((w) => w.stockOutId === b.stockOutId) || {}),
        ...b,
      }));
      if (blocked.length > 0) {
        setConfirm({ eventId, status: 'locked', event: (shown && shown.event) || ev, blocked, notice: null });
        return;
      }
    }

    setConfirm(null);
    setError(res.error);
    // Permanently out of bounds — drop it so it cannot be tried again. A row
    // that will always be refused is worse than no row: it reads as a bug.
    if (res.code === 'PERIOD_RECONCILED' || res.code === 'OUTSIDE_REVERT_WINDOW') {
      setEvents((prev) => prev.filter((e) => e.eventId !== eventId));
    }
  };

  const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const dmy = (iso) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(iso || '');
  };

  const closeOnly = (
    <div style={{ display: 'flex', marginTop: '10px' }}>
      <button type="button" onClick={closeConfirm} style={secondaryBtn}>Close</button>
    </div>
  );

  const renderConfirm = (ev) => {
    const c = confirm;
    const busy = revertingId === ev.eventId;
    const notice = c.notice ? <div style={noticeStyle}>{c.notice}</div> : null;
    const event = c.event || ev;

    if (c.status === 'loading') {
      return <div style={{ ...panelStyle, color: '#6b7280' }}>Checking what this revert includes…</div>;
    }

    if (c.status === 'error') {
      return (
        <div style={panelStyle}>
          <div style={{ color: '#991b1b', fontWeight: 600 }}>{c.error}</div>
          {closeOnly}
        </div>
      );
    }

    if (c.status === 'refused') {
      return (
        <div style={panelStyle}>
          <div style={{ color: '#991b1b', fontWeight: 600 }}>{c.reason}</div>
          {closeOnly}
        </div>
      );
    }

    if (c.status === 'locked') {
      return (
        <div style={{ ...panelStyle, background: '#fef2f2', borderColor: '#fecaca' }}>
          <p style={{ margin: '0 0 6px' }}>
            <strong>{`${event.functionName || 'This banquet'} can't be reverted.`}</strong>
          </p>
          {c.blocked.map((b) => (
            <p key={b.stockOutId || b.itemName} style={{ margin: '0 0 4px', color: '#7f1d1d' }}>
              {lockedEntrySentence(b)}
            </p>
          ))}
          {closeOnly}
        </div>
      );
    }

    if (c.status === 'wastage') {
      return (
        <div style={panelStyle}>
          {notice}
          <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{revertQuestion(event)}</p>
          <p style={{ margin: '0 0 6px' }}>{wastageIntro(c.wastage.length, c.wastageTotal)}</p>
          <ul style={{ margin: '0 0 8px', paddingLeft: '18px' }}>
            {c.wastage.map((w) => (
              <li key={w.stockOutId}>{wastageEntryLine(w)}</li>
            ))}
          </ul>
          <p style={{ margin: '0 0 2px' }}>Stock will be restored for the banquet and for this wastage.</p>
          <p style={{ margin: 0, color: '#6b7280' }}>Wastage recorded for other banquets is not touched.</p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button type="button" onClick={closeConfirm} disabled={busy} style={secondaryBtn}>
              Cancel
            </button>
            <button type="button" onClick={() => doRevert(ev)} disabled={busy} style={dangerBtn}>
              {busy ? 'Reverting…' : 'Confirm & Revert Both'}
            </button>
          </div>
        </div>
      );
    }

    // 'plain' — no wastage: the confirm as it always was.
    return (
      <div style={{ marginTop: '10px' }}>
        {notice}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={closeConfirm} disabled={busy} style={secondaryBtn}>
            Cancel
          </button>
          <button type="button" onClick={() => doRevert(ev)} disabled={busy} style={dangerBtn}>
            {busy ? 'Reverting…' : 'Confirm revert'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bq-modal-overlay" onClick={onClose}>
      <div
        className="bq-modal"
        style={{ maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '22px' }}>↩</span>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111' }}>Revert Banquet</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6b7280', lineHeight: 1 }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '18px' }}>
          Removes the booking and puts back the stock it used, so it can be entered again.
        </p>

        {success && (
          <div style={{ background: '#fff', border: '1px solid #111', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
            <p style={{ color: '#111', fontWeight: 700, marginBottom: '6px' }}>
              {revertSuccessHeadline({
                ingredients: success.stockRestored ? success.stockRestored.ingredients : 0,
                wastageCount: success.wastageCount,
              })}
            </p>
            {success.wastageCount > 0 && (
              <p style={{ color: '#374151', fontSize: '0.85rem', marginBottom: '8px' }}>
                If this wastage still applies, record it again against the corrected banquet.
              </p>
            )}
            <div style={{ color: '#111', fontSize: '0.85rem' }}>
              <div>Function: <strong>{success.functionName}</strong></div>
              <div>Date: <strong>{dmy(success.eventDate)}</strong></div>
              <div>Plates: <strong>{success.platesOrdered}</strong></div>
              <div>Bill: <strong>{money(success.totalBill)}</strong></div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{ marginTop: '12px', width: '100%', padding: '10px', background: '#111', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        )}

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px', marginBottom: '16px', color: '#991b1b', fontSize: '0.88rem', fontWeight: 600 }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
            Loading recent banquets…
          </div>
        )}

        {!loading && !success && (
          <>
            {window_ && (
              <div style={{
                fontSize: '0.8rem', color: '#374151', background: '#f8fafc',
                border: '1px solid #e5e7eb', borderRadius: '8px',
                padding: '8px 12px', marginBottom: '12px', lineHeight: 1.5,
              }}>
                {window_.basis === 'closing_count' ? (
                  <>Reverting is open for banquets <strong>after {window_.floor}</strong> — the last confirmed
                    closing stock count. Anything on or before that date is certified and locked.</>
                ) : (
                  <>No closing stock count yet, so reverting is limited to the <strong>last 30 days</strong> (from {window_.floor}).</>
                )}
              </div>
            )}

            {events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px', color: '#6b7280', fontSize: '0.88rem' }}>
                {/* Two different facts, said differently — see the same note in
                    the TSR revert modal. */}
                {window_ && window_.basis === 'closing_count'
                  ? 'No banquets recorded since the last closing stock count.'
                  : 'No banquets recorded in the last 30 days.'}
              </div>
            ) : (
              events.map((ev) => (
                <div
                  key={ev.eventId}
                  style={{
                    border: '1px solid #e5e7eb', borderRadius: '10px',
                    padding: '12px 14px', marginBottom: '10px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#111', fontSize: '0.95rem' }}>{ev.functionName}</div>
                      <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px' }}>
                        {dmy(ev.eventDate)} · {ev.platesOrdered} plates · {ev.itemCount} item{ev.itemCount === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, color: '#111', whiteSpace: 'nowrap' }}>{money(ev.totalBill)}</div>
                  </div>

                  {confirm && confirm.eventId === ev.eventId ? (
                    renderConfirm(ev)
                  ) : (
                    <button
                      type="button"
                      onClick={() => openConfirm(ev)}
                      disabled={revertingId !== null}
                      style={{ marginTop: '10px', width: '100%', padding: '9px', borderRadius: '8px', border: '1.5px solid #fecaca', background: '#fff', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}
                    >
                      ↩ Revert this banquet
                    </button>
                  )}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BanquetRevertModal;
